import React, { useEffect, useRef, useState } from 'react';
import style from './upload.module.scss';
import map from 'lodash-es/map';
import isEmpty from 'lodash-es/isEmpty';
import size from 'lodash-es/size';
import values from 'lodash-es/values';
import { useForm, FormProvider } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectCategories, uploadRequest } from 'store/page/pageSlice';
import FormTabs from 'controls/tabs/formTabs';
import SubmitButton from 'controls/buttons/submit';
import { useSearchControl } from 'hooks/useSearchControl';
import ListItem from 'components/uploadForm/listItem/listItem';
import ErrorModal from 'components/modal/errorModal';
import cx from 'classnames';
import {
  selectUploadedFiles,
  setGlobalModal,
  setUploadedFiles,
} from 'store/transitionSlice';
import ProgressModal, { ProgressItems } from 'components/modal/progressModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTaggedFileList } from 'hooks/files/useTaggedFileList';
import processFiles from './processFiles';
import HelperSearch from 'controls/search/helperSearch/helperSearch';
import { useFileList } from 'hooks/files/useFileList';
import ListItemAdd from 'components/uploadForm/addListItem/addItem';
import findKey from 'lodash-es/findKey';
import omit from 'lodash-es/omit';
import cloneDeep from 'lodash-es/cloneDeep';
import { getGrop, Group } from './getGroup';
import { Detection, getHeaderTexts } from './detection/detection';
import GlobalCheck from './globalCheck/globalCheck';
import { CategoryId, FileItem } from 'store/types';
import { TransitionGroup } from 'react-transition-group';
import { DURATIONS } from 'utils/utils';
import { getMeshKey, useGrid } from 'hooks/useGrid';
import { useWindowSize } from 'hooks/useWindowSize';

// Сопоставление названия превьюхи и файла
export type PreviewMap = { [x: string]: string };

export type ItemStatus = 'success' | 'warning' | 'error';

export default function Upload() {
  const dispatch = useAppDispatch();
  const categories = useAppSelector(selectCategories);
  const [isUploading, setIsUploading] = useState(false);
  const [progressItems, setProgressItems] = useState<ProgressItems>({});
  const navigate = useNavigate();
  const { hash } = useLocation();
  const resContainerRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();

  const URLCategory = decodeURI(hash).slice(1);

  // fakeChecked нужен из за костыля в работе кнопки отменить все,
  // Так как она сначала выбирает все в обход батчинга, а затем отменяет
  const [fakeChecked, setFakeChecked] = useState(false);
  // Изменение globalChecked принудительно выставляет чекбоксы в соответствии с ним
  const [globalChecked, setGlobalChecked] = useState(false);
  // isAllChecked - говорит о текущем состоянии, так как после принудительного выставления через globalChecked
  // Внутреннее состояние чекбокса может меняться действиями пользователя
  const [isAllChecked, setIsAllChecked] = useState(false);

  const [emptyTagsFail, setEmptyTagsFail] = useState(false);

  // Список файлов
  const {
    list: fileList,
    onDrop: onFileDrop,
    onBrowse: onFileBrowse,
    getByKeys: fileGetByKeys,
    removeFile: fileRemoveFile,
    removeAllFiles: fileRemoveAllFiles,
    validate: fileValidate,
    addTag,
    removeTag,
    pushItems,
  } = useTaggedFileList();
  const initial = cloneDeep(useAppSelector(selectUploadedFiles));

  useEffect(() => {
    pushItems(initial);
    dispatch(setUploadedFiles({}));
  }, []);

  // Список preview
  const {
    list: previewList,
    removeFile: previewRemoveFile,
    removeAllFiles: previewRemoveAllFiles,
    ...otherListParams
  } = useFileList();

  // Карта связи оригинального файла с preview, ключь - id файла, значение - id preview
  const [previewMap, setPreviewMap] = useState<PreviewMap>({});

  // Выбранные чекбоксами файлы оборачиваются в группы
  const [group, setGroup] = useState<Group>({
    groupId: '',
    groupTags: {},
    items: {},
  });

  const detectionTexts = getHeaderTexts(size(group.items));

  const updateGroup = () => {
    const items = fileGetByKeys(methods.getValues()); // Ключи полей формы совпавшие с ключами общего списка файлов - есть выбранные
    if (!fakeChecked && size(items) && size(items) === size(fileList)) {
      setIsAllChecked(true);
    } else {
      setIsAllChecked(false);
    }
    setGroup(getGrop(items));
  };

  useEffect(updateGroup, [fileList]);

  // Каждый компонент listItem связан с основным файлом по id и хранит внутреннее состояние
  const [itemsStatus, setItemsStatus] = useState<{
    [x: string]: ItemStatus;
  }>({});
  const hasWrong = () =>
    findKey(itemsStatus, (val) => {
      return val !== 'success';
    })
      ? true
      : false;
  const [isWrong, setIsWrong] = useState(hasWrong());

  useEffect(() => {
    setIsWrong(hasWrong());
  }, [itemsStatus]);

  const searchDisabled = isEmpty(fileList) || isEmpty(group.groupId);
  const submitDisabled = isEmpty(fileList) || isWrong;

  // Форма
  const methods = useForm();
  const currentCategory =
    (methods.watch('category') as CategoryId) || URLCategory;

  const onSubmit = async () => {
    const validation = fileValidate();

    if (validation === 'success') {
      await processFiles({
        setProgressItems,
        setIsUploading,
        fileList,
        previewList,
        previewMap,
        category: currentCategory,
        onRequestInit: (promise) => {
          dispatch(uploadRequest(promise));
        },
      });

      return;
    }

    switch (validation) {
      case 'empty':
        dispatch(
          setGlobalModal(
            <ErrorModal
              onClose={() => dispatch(setGlobalModal(null))}
              errorText="Загружаемый контент отсутствует, пожалуйста, загрузите файлы и попробуйте снова"
            ></ErrorModal>
          )
        );
        return;
      case 'empty_tag':
        dispatch(
          setGlobalModal(
            <ErrorModal
              onClose={() => dispatch(setGlobalModal(null))}
              errorText="У каждого загружаемого элемента должен быть хотя бы один тег"
            ></ErrorModal>
          )
        );
        setEmptyTagsFail(true);
        return;
    }
  };

  // значения по умолчанию
  let capacity = 3;
  const coeff = 200 / 285;

  const itemWidth =
    windowSize.width >= 1920 ? 330 : windowSize.width >= 1440 ? 285 : 240;
  const itemHeight = coeff * itemWidth;
  const gap = 10;
  let containerWidth = itemWidth * capacity + gap * (capacity - 1);

  if (resContainerRef.current) {
    containerWidth = resContainerRef.current.getBoundingClientRect().width;
  }

  // Пересчитаем сколько item-ов влезает целиком
  capacity = Math.floor(containerWidth / itemWidth);
  // Получим поддерживаемый модулем грида размер сетки
  const meshSize = getMeshKey(capacity);
  const meshSizeNum = Number(meshSize.trim());

  // Скорректируем размер контейнера ровно под размер сетки
  if (resContainerRef.current) {
    resContainerRef.current.style.width = `${
      meshSizeNum * itemWidth + gap * (meshSizeNum - 1)
    }px`;
    containerWidth = resContainerRef.current.getBoundingClientRect().width;
  }

  // TODO: Здесь происходит множество бесконечных перерасчетов, но они не вносят видимого замедления, пока что
  const { items, conteinerHeight } = useGrid({
    mesh: {
      [meshSize]: itemHeight,
    },
    containerWidth,
    gap,
    // квадратные скобки недопустимы в названии, поэтому можно вкостылить фейковый item для add
    items: [...values(fileList), { id: '[add-item]' } as FileItem],
    duration: DURATIONS.GRID_ITEM,
    constructItem: (item) => {
      return item.id === '[add-item]' ? (
        <ListItemAdd
          currentCategory={currentCategory}
          onChange={onFileBrowse}
          onFileDrop={onFileDrop}
        />
      ) : (
        <ListItem
          item={item}
          onCheckboxChange={updateGroup}
          onDelete={(fileName, previewName) => {
            methods.unregister(item.id as `${string}`);
            fileRemoveFile(fileName);
            previewRemoveFile(previewName);
            setItemsStatus((old) => {
              return omit(old, fileName);
            });
          }}
          previewList={{
            list: previewList,
            removeFile: previewRemoveFile,
            removeAllFiles: previewRemoveAllFiles,
            ...otherListParams,
          }}
          previewMap={previewMap}
          setPreviewMap={setPreviewMap}
          currentCategory={currentCategory}
          onStatusChange={setItemsStatus}
          globalChecked={globalChecked}
          emptyTagsFail={emptyTagsFail}
        />
      );
    },
  });

  // Корректировка высоты контейнера
  useEffect(() => {
    if (resContainerRef.current && conteinerHeight > 0) {
      resContainerRef.current.style.height = `${conteinerHeight + 74}px`;
    }
  }, [conteinerHeight]);

  // Если меняется ширина окна, нужно сбросить фиксированную ширину контейнера\
  // Чтобы сетка пересчиталась снова
  useEffect(() => {
    if (resContainerRef.current) {
      resContainerRef.current.style.width = `auto`;
    }
  }, [windowSize.width]);

  return (
    <>
      {size(progressItems) ? (
        <ProgressModal
          onContinue={() => {
            navigate(`/cabinet`, {
              replace: false,
            });
          }}
          onAgain={() => {
            setProgressItems({});
            fileRemoveAllFiles();
            previewRemoveAllFiles();
          }}
          progressItems={progressItems}
        ></ProgressModal>
      ) : (
        ''
      )}
      <FormProvider {...methods}>
        <form
          className={cx(style.container, 'adaptive-upload__container')}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <span className={style.loadHeader}>
            Загрузите и отредактируйте свой медиаконтент
          </span>
          <div className={style.loadArea} ref={resContainerRef}>
            <GlobalCheck
              setGlobalChecked={setGlobalChecked}
              isAllChecked={isAllChecked}
              setIsAllChecked={setIsAllChecked}
              setFakeChecked={setFakeChecked}
            ></GlobalCheck>

            <div className={style.upload}>
              <div className={style.list}>
                <TransitionGroup component={null}>{items}</TransitionGroup>
              </div>
            </div>
          </div>

          <div className={cx(style.metaArea, 'adaptive-upload__metaArea')}>
            <span className={style.mediaHeader}>
              Выберите тип медиаконтента
            </span>
            <FormTabs<CategoryId>
              groupName="category"
              defaultValue={URLCategory}
              variants={map(categories, (c) => {
                return {
                  id: c.id,
                  value: c.id,
                  text: c.id,
                };
              })}
              onChange={(e, value) => {
                location.hash = value;
              }}
            ></FormTabs>

            <div
              className={cx(
                style.searchSection,
                searchDisabled ? style.searchSectionDisabled : '',
                'adaptive-upload__searchSection'
              )}
            >
              <div
                className={cx(
                  style.tagsText,
                  searchDisabled ? 'blockDisabled' : ''
                )}
              >
                <span>{`${detectionTexts.lightText}`}&nbsp;</span>
                <b>
                  <span>{detectionTexts.boldText}</span>
                </b>
              </div>

              <HelperSearch
                isDisabled={searchDisabled}
                className={style.search}
                tagsStrategy={useSearchControl.bind(null, {
                  forceTags: group.groupTags,
                  onSelect: (tag) => {
                    addTag(
                      tag,
                      map(group.items, (item) => item.id)
                    );
                  },
                  onDelete: (tag) => {
                    removeTag(
                      tag,
                      map(group.items, (item) => item.id)
                    );
                  },
                })}
              ></HelperSearch>

              {group.groupId ? (
                <Detection
                  commonTags={size(group.groupTags)}
                  itemsInGroup={size(group.items)}
                ></Detection>
              ) : (
                ''
              )}
              <div className={style.submit}>
                <SubmitButton
                  disabled={submitDisabled}
                  wrapper={{
                    loading: isUploading,
                  }}
                >
                  {!isUploading && 'Сохранить изменения'}
                </SubmitButton>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
